import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, Eye, EyeOff, Users, Lock, Unlock, Clock, 
  FileText, AlertTriangle, CheckCircle, History, Plus,
  User, Home, UserPlus, Settings, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Visibility level colors
const VISIBILITY_COLORS = {
  full: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Full Access' },
  summary: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Summary Only' },
  hidden: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Hidden' },
  masked: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Masked' }
};

// Data category labels
const CATEGORY_LABELS = {
  grades: { label: 'Grades & GPA', icon: FileText, sensitive: false },
  test_scores: { label: 'Test Scores', icon: FileText, sensitive: false },
  journal_notes: { label: 'Journal Notes', icon: FileText, sensitive: true },
  mental_wellness: { label: 'Mental Wellness', icon: Shield, sensitive: true },
  career_interests: { label: 'Career Interests', icon: FileText, sensitive: false },
  college_list: { label: 'College List', icon: FileText, sensitive: false },
  financial_aid: { label: 'Financial Aid', icon: FileText, sensitive: true },
  applications: { label: 'Applications', icon: FileText, sensitive: false },
  deadlines: { label: 'Deadlines', icon: Clock, sensitive: false },
  documents: { label: 'Documents', icon: FileText, sensitive: true },
  activity_log: { label: 'Activity Log', icon: History, sensitive: false },
  goals: { label: 'Goals', icon: FileText, sensitive: false },
  path_selections: { label: 'Path Selections', icon: FileText, sensitive: false }
};

// Guardian role labels
const GUARDIAN_ROLES = {
  primary_parent: { label: 'Primary Parent', color: 'emerald' },
  co_parent: { label: 'Co-Parent', color: 'blue' },
  guardian: { label: 'Guardian', color: 'purple' },
  trusted_adult: { label: 'Trusted Adult', color: 'amber' },
  counselor: { label: 'Counselor', color: 'slate' }
};

// Sharing Preference Card
const SharingCard = ({ preference, onUpdate, isStudent, ageGroup, lockableCategories }) => {
  const category = CATEGORY_LABELS[preference.category];
  const visibility = VISIBILITY_COLORS[preference.visibility];
  const Icon = category?.icon || FileText;
  
  // Check if this category can be locked by the student based on age
  const canLock = lockableCategories?.includes(preference.category);
  const canEdit = isStudent && canLock;
  const isLocked = !canLock && ageGroup === 'minor';
  
  return (
    <div className={`p-4 rounded-xl border transition-colors ${
      isLocked 
        ? 'bg-slate-800/30 border-slate-700/50' 
        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isLocked ? 'bg-slate-700/50' : 'bg-slate-700'
          }`}>
            <Icon className={`w-5 h-5 ${isLocked ? 'text-slate-300' : 'text-slate-300'}`} />
          </div>
          <div>
            <p className={`font-medium ${isLocked ? 'text-slate-300' : 'text-white'}`}>
              {category?.label || preference.category}
            </p>
            {canLock && (
              <span className="text-xs text-emerald-400">You can hide this</span>
            )}
            {isLocked && (
              <span className="text-xs text-slate-300">Parent always sees this</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isLocked && (
            <Lock className="w-4 h-4 text-slate-300" />
          )}
          <Badge className={`${visibility.bg} ${visibility.text} border-none`}>
            {visibility.label}
          </Badge>
        </div>
      </div>
      
      {isStudent && (
        <div className="flex gap-2">
          {canLock ? (
            // Student can toggle this category
            ['full', 'hidden'].map((level) => (
              <button
                key={level}
                onClick={() => onUpdate(preference.category, level)}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${
                  preference.visibility === level
                    ? `${VISIBILITY_COLORS[level].bg} ${VISIBILITY_COLORS[level].text}`
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {level === 'full' && <Eye className="w-3 h-3 inline mr-1" />}
                {level === 'hidden' && <Lock className="w-3 h-3 inline mr-1" />}
                {level === 'full' ? 'Share with Parent' : 'Keep Private'}
              </button>
            ))
          ) : (
            // Category is locked - student cannot change
            <div className="w-full py-2 px-3 rounded-lg bg-slate-700/50 text-slate-300 text-xs text-center">
              <Lock className="w-3 h-3 inline mr-1" />
              {ageGroup === 'minor' 
                ? 'Available to hide at age 17+' 
                : 'Parent visibility required'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Guardian Card
const GuardianCard = ({ guardian, onRemove }) => {
  const role = GUARDIAN_ROLES[guardian.role] || GUARDIAN_ROLES.guardian;
  
  return (
    <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full bg-${role.color}-500/20 flex items-center justify-center`}>
            <User className={`w-6 h-6 text-${role.color}-400`} />
          </div>
          <div>
            <p className="text-white font-semibold">{guardian.guardian_name}</p>
            <p className="text-slate-300 text-sm">{guardian.guardian_email}</p>
          </div>
        </div>
        <Badge className={`bg-${role.color}-500/20 text-${role.color}-400 border-none`}>
          {role.label}
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-2 h-2 rounded-full ${guardian.can_view_applications ? 'bg-emerald-400' : 'bg-slate-600'}`} />
          <span className="text-slate-300">View Applications</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-2 h-2 rounded-full ${guardian.can_edit_financial ? 'bg-emerald-400' : 'bg-slate-600'}`} />
          <span className="text-slate-300">Edit Financial</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-2 h-2 rounded-full ${guardian.can_receive_notifications ? 'bg-emerald-400' : 'bg-slate-600'}`} />
          <span className="text-slate-300">Notifications</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-2 h-2 rounded-full ${guardian.is_active ? 'bg-emerald-400' : 'bg-slate-600'}`} />
          <span className="text-slate-300">Active</span>
        </div>
      </div>
      
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => onRemove(guardian.id)}>
          Remove Access
        </Button>
      </div>
    </div>
  );
};

// Audit Log Entry
const AuditEntry = ({ entry }) => {
  const getActionIcon = (action) => {
    switch (action) {
      case 'permission_changed': return <Shield className="w-4 h-4 text-blue-400" />;
      case 'document_uploaded': return <FileText className="w-4 h-4 text-emerald-400" />;
      case 'guardian_added': return <UserPlus className="w-4 h-4 text-purple-400" />;
      case 'guardian_removed': return <User className="w-4 h-4 text-red-400" />;
      default: return <History className="w-4 h-4 text-slate-300" />;
    }
  };
  
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/30">
      <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
        {getActionIcon(entry.action)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm">{entry.description}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-slate-300 text-xs">by {entry.actor_name}</span>
          <span className="text-slate-300 text-xs">•</span>
          <span className="text-slate-300 text-xs">{formatDate(entry.timestamp)}</span>
        </div>
      </div>
    </div>
  );
};

// Add Guardian Modal
const AddGuardianModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    guardian_name: '',
    guardian_email: '',
    role: 'co_parent',
    can_view_applications: true,
    can_edit_financial: false,
    can_receive_notifications: true
  });
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-[#0a1729]/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md"
      >
        <div className="p-6 border-b border-slate-800">
          <h2 className="text-xl font-bold text-white">Add Guardian Access</h2>
          <p className="text-slate-300 text-sm mt-1">Invite a co-parent, guardian, or trusted adult</p>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="text-white text-sm mb-2 block">Name</label>
            <input
              type="text"
              value={formData.guardian_name}
              onChange={(e) => setFormData(prev => ({ ...prev, guardian_name: e.target.value }))}
              className="w-full p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
              placeholder="Enter guardian name"
            />
          </div>
          
          <div>
            <label className="text-white text-sm mb-2 block">Email</label>
            <input
              type="email"
              value={formData.guardian_email}
              onChange={(e) => setFormData(prev => ({ ...prev, guardian_email: e.target.value }))}
              className="w-full p-3 rounded-lg bg-slate-800 border border-slate-700 text-white"
              placeholder="Enter guardian email"
            />
          </div>
          
          <div>
            <label className="text-white text-sm mb-2 block">Role</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(GUARDIAN_ROLES).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => setFormData(prev => ({ ...prev, role: key }))}
                  className={`p-3 rounded-lg border text-sm transition-colors ${
                    formData.role === key
                      ? `border-${value.color}-500 bg-${value.color}-500/20 text-${value.color}-400`
                      : 'border-slate-700 text-slate-300 hover:border-slate-600'
                  }`}
                >
                  {value.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="space-y-3">
            <label className="text-white text-sm block">Permissions</label>
            <div className="flex items-center justify-between">
              <span className="text-slate-300 text-sm">Can view applications</span>
              <Switch 
                checked={formData.can_view_applications}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, can_view_applications: checked }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-300 text-sm">Can edit financial info</span>
              <Switch 
                checked={formData.can_edit_financial}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, can_edit_financial: checked }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-300 text-sm">Receive notifications</span>
              <Switch 
                checked={formData.can_receive_notifications}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, can_receive_notifications: checked }))}
              />
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} className="border-slate-600">Cancel</Button>
          <Button 
            onClick={() => { onSubmit(formData); onClose(); }}
            disabled={!formData.guardian_name || !formData.guardian_email}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Add Guardian
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Main Component
export default function PrivacySettings() {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [activeTab, setActiveTab] = useState('sharing');
  const [showAddGuardian, setShowAddGuardian] = useState(false);
  
  const isStudent = user?.role === 'student';
  const studentId = isStudent ? user?.id : user?.linked_children?.[0]?.child_id;
  
  useEffect(() => {
    if (studentId) {
      fetchSettings();
      fetchAuditLog();
    }
  }, [studentId]);
  
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/privacy/settings/${studentId}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch privacy settings:', error);
      toast.error('Failed to load privacy settings');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAuditLog = async () => {
    try {
      const response = await axios.get(`${API}/privacy/audit-log/${studentId}?limit=20`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setAuditLog(response.data.audit_log || []);
    } catch (error) {
      console.error('Failed to fetch audit log:', error);
    }
  };
  
  const handleUpdatePreference = async (category, visibility) => {
    try {
      const newPreferences = settings.sharing_preferences.map(p => 
        p.category === category ? { ...p, visibility } : p
      );
      
      await axios.put(`${API}/privacy/settings/${studentId}/preferences`, newPreferences, {
        ...getAuthHeader(),
        withCredentials: true
      });
      
      setSettings(prev => ({ ...prev, sharing_preferences: newPreferences }));
      toast.success('Privacy settings updated');
      fetchAuditLog();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };
  
  const handleAddGuardian = async (guardianData) => {
    try {
      await axios.post(`${API}/privacy/guardians/${studentId}`, guardianData, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Guardian added successfully');
      fetchSettings();
      fetchAuditLog();
    } catch (error) {
      toast.error('Failed to add guardian');
    }
  };
  
  const handleRemoveGuardian = async (guardianId) => {
    if (!confirm('Are you sure you want to remove this guardian?')) return;
    
    try {
      await axios.delete(`${API}/privacy/guardians/${studentId}/${guardianId}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Guardian removed');
      fetchSettings();
      fetchAuditLog();
    } catch (error) {
      toast.error('Failed to remove guardian');
    }
  };
  
  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }
  
  const ageGroup = settings?.current_age_group || 'minor';
  const lockableCategories = settings?.lockable_categories || [];
  
  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Privacy & Sharing</h1>
              <p className="text-slate-300">Control what parents and guardians can see</p>
            </div>
          </div>
          
          {/* Age-based notice */}
          <div className={`mt-4 p-4 rounded-xl ${
            ageGroup === 'adult' ? 'bg-emerald-500/10 border border-emerald-500/20' :
            ageGroup === 'upperclass' ? 'bg-blue-500/10 border border-blue-500/20' :
            'bg-amber-500/10 border border-amber-500/20'
          }`}>
            <div className="flex items-center gap-2">
              {ageGroup === 'adult' ? (
                <>
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">Full Control (18+)</span>
                </>
              ) : ageGroup === 'upperclass' ? (
                <>
                  <Shield className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-400 font-medium">Senior Year (17)</span>
                </>
              ) : (
                <>
                  <Users className="w-5 h-5 text-amber-400" />
                  <span className="text-amber-400 font-medium">Parent Account (6th-11th Grade)</span>
                </>
              )}
            </div>
            <p className="text-slate-300 text-sm mt-1">
              {ageGroup === 'adult' 
                ? "You have full control over your privacy settings. You can block all information from parents if you choose."
                : ageGroup === 'upperclass'
                ? "As a senior, you can hide Journal Notes and Mental Wellness content. Other information remains visible to parents."
                : "Parents can see most of your information. You can only choose to hide your Journal Notes."
              }
            </p>
            {ageGroup !== 'adult' && (
              <p className="text-slate-300 text-xs mt-2">
                <Lock className="w-3 h-3 inline mr-1" />
                You can hide: {lockableCategories.length > 0 
                  ? lockableCategories.map(c => CATEGORY_LABELS[c]?.label || c).join(', ')
                  : 'Journal Notes only'}
              </p>
            )}
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full bg-slate-800/50 p-1 mb-6 grid grid-cols-3">
            <TabsTrigger value="sharing" className="data-[state=active]:bg-blue-600">
              <Eye className="w-4 h-4 mr-2" /> Data Sharing
            </TabsTrigger>
            <TabsTrigger value="guardians" className="data-[state=active]:bg-blue-600">
              <Users className="w-4 h-4 mr-2" /> Guardians
            </TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-blue-600">
              <History className="w-4 h-4 mr-2" /> Activity Log
            </TabsTrigger>
          </TabsList>
          
          {/* Data Sharing Tab */}
          <TabsContent value="sharing">
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">What Parents Can See</h3>
              <p className="text-slate-300 text-sm mb-6">
                Control the visibility of different types of information. Changes take effect immediately.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                {settings?.sharing_preferences?.map((pref) => (
                  <SharingCard
                    key={pref.category}
                    preference={pref}
                    onUpdate={handleUpdatePreference}
                    isStudent={isStudent}
                    ageGroup={ageGroup}
                    lockableCategories={lockableCategories}
                  />
                ))}
              </div>
            </GlassCard>
          </TabsContent>
          
          {/* Guardians Tab */}
          <TabsContent value="guardians">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Family Access</h3>
                <Button onClick={() => setShowAddGuardian(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Guardian
                </Button>
              </div>
              
              {settings?.guardians?.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {settings.guardians.map((guardian) => (
                    <GuardianCard
                      key={guardian.id}
                      guardian={guardian}
                      onRemove={handleRemoveGuardian}
                    />
                  ))}
                </div>
              ) : (
                <GlassCard className="p-8 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="text-white font-semibold mb-2">No Additional Guardians</h4>
                  <p className="text-slate-300 text-sm">
                    Add a co-parent, guardian, or trusted adult to share access
                  </p>
                </GlassCard>
              )}
              
              {/* Households section for custody situations */}
              {settings?.households?.length > 0 && (
                <GlassCard className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Home className="w-5 h-5 text-purple-400" />
                    Household Configuration
                  </h3>
                  <div className="space-y-3">
                    {settings.households.map((household) => (
                      <div key={household.id} className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                        <p className="text-white font-medium">{household.name}</p>
                        <p className="text-slate-300 text-sm">{household.type} household</p>
                        {household.financial_visibility_boundary && (
                          <Badge className="mt-2 bg-amber-500/20 text-amber-400">
                            Separate Financial View
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </GlassCard>
              )}
            </div>
          </TabsContent>
          
          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Activity Log</h3>
              <p className="text-slate-300 text-sm mb-6">
                Track all changes to your privacy settings and account access
              </p>
              
              {auditLog.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {auditLog.map((entry) => (
                    <AuditEntry key={entry.id} entry={entry} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-300">No activity logged yet</p>
                </div>
              )}
            </GlassCard>
          </TabsContent>
        </Tabs>
        
        {/* Add Guardian Modal */}
        <AddGuardianModal
          isOpen={showAddGuardian}
          onClose={() => setShowAddGuardian(false)}
          onSubmit={handleAddGuardian}
        />
      </div>
    </AppLayout>
  );
}
